import React from 'react'
import { useTranslation } from 'react-i18next'
import { MultiSelectFilter, type FilterOption } from '@/components/ui/multi-select-filter'
import { sdk } from '@/lib/sdk'
import type { SupportSelection } from '@/types/support'
import { useTeamOptions } from './supportUtils'

interface Lists {
  tags: FilterOption[]
  groups: FilterOption[]
}

/** Tags and groups of the community, loaded once for every picker on the screen. */
export const useResponderLists = (): Lists & { loading: boolean } => {
  const [lists, setLists] = React.useState<Lists | null>(null)
  React.useEffect(() => {
    let alive = true
    Promise.all([
      sdk.members.listTags().catch(() => []),
      sdk.memberGroups.list().catch(() => [])
    ]).then(([tags, groups]) => {
      if (!alive) return
      setLists({
        tags: (tags as Array<{ tag: string; count: number; color: string | null }>).map((x) => ({ value: x.tag, label: x.tag, count: x.count, color: x.color })),
        groups: ((Array.isArray(groups) ? groups : []) as Array<{ id: string; name: string }>).map((g) => ({ value: g.id, label: g.name }))
      })
    })
    return () => { alive = false }
  }, [])
  return { tags: lists?.tags ?? [], groups: lists?.groups ?? [], loading: lists === null }
}

interface Props {
  value: SupportSelection
  onChange: (value: SupportSelection) => void
  lists: Lists
  disabled?: boolean
}

/**
 * Who answers: any mix of member tags (by name), groups and team people. The three pickers combine; the
 * list is resolved by the server when it reads, and owner and admin are always in it.
 */
export const ResponderPicker = ({ value, onChange, lists, disabled }: Props) => {
  const { t } = useTranslation('support')
  const team = useTeamOptions(true)
  const people: FilterOption[] = team.options.map((o) => ({ value: o.id, label: o.name }))

  return (
    <div className="grid gap-2" data-testid="support-responder-picker">
      <MultiSelectFilter label={t('config.responders.tags')} options={lists.tags} selected={value.tags} onChange={(tags) => onChange({ ...value, tags })} emptyLabel={t('config.responders.noTags')} disabled={disabled} />
      <MultiSelectFilter label={t('config.responders.groups')} options={lists.groups} selected={value.groupIds} onChange={(groupIds) => onChange({ ...value, groupIds })} emptyLabel={t('config.responders.noGroups')} disabled={disabled} />
      <MultiSelectFilter label={t('config.responders.people')} options={people} selected={value.memberIds} onChange={(memberIds) => onChange({ ...value, memberIds })} emptyLabel={t('config.responders.noPeople')} disabled={disabled} />
    </div>
  )
}
